import { Box, Button, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { shuffle } from 'lodash'; // Importing the shuffle function from a library like Lodash
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { hexToRgbaColorString } from '../../Score';
import { DialogPromptQuizWord } from '../../api/DialogPromptApi';
import { UserDialogPromptApi } from '../../api/UserDialogPromptApi';
import AppBarContext from '../../context/AppBarContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import { getAudioByText } from './DialogPromptComponent';


interface DialogPromptQuizWordComponentProps {
    dialogPrompt: DialogPromptQuizWord;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

const DialogPromptQuizWordComponent: React.FC<DialogPromptQuizWordComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef, isSettings = false }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [componentList, setComponentList] = React.useState<any[]>([]);
    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);
    const [chatBubbleCorrectDone, setChatBubbleCorrectDone] = React.useState(false);
    const [chatBubbleIncorrectDone, setChatBubbleIncorrectDone] = React.useState(false);

    // const kaiTiFontClass = useKaiTiFontStyles();

    const theme = useTheme();

    type WordChoice = {
        word: string;
        correct: boolean;
        id: string;
    };

    const wordChoices = React.useMemo<WordChoice[]>(() => [
        { word: dialogPrompt.new_word, correct: true, id: 'correct' },
        dialogPrompt.wrong_word_1 ? { word: dialogPrompt.wrong_word_1, correct: false, id: 'wrong-1' } : null,
        dialogPrompt.wrong_word_2 ? { word: dialogPrompt.wrong_word_2, correct: false, id: 'wrong-2' } : null,
        dialogPrompt.wrong_word_3 ? { word: dialogPrompt.wrong_word_3, correct: false, id: 'wrong-3' } : null,
        dialogPrompt.wrong_word_4 ? { word: dialogPrompt.wrong_word_4, correct: false, id: 'wrong-4' } : null,
        dialogPrompt.wrong_word_5 ? { word: dialogPrompt.wrong_word_5, correct: false, id: 'wrong-5' } : null,
    ].filter(Boolean) as WordChoice[], [
        dialogPrompt.new_word,
        dialogPrompt.wrong_word_1,
        dialogPrompt.wrong_word_2,
        dialogPrompt.wrong_word_3,
        dialogPrompt.wrong_word_4,
        dialogPrompt.wrong_word_5,
    ]);

    const renderWordRef = React.useRef<(choice: WordChoice, displayedChoices: WordChoice[], interactive: boolean, selectedChoice?: WordChoice) => React.ReactNode>(() => null);

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [yesResponseAudio, setYesResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [noResponseAudio, setNoResponseAudio] = React.useState<Blob | null | undefined>(undefined);

    const handleWordClick = React.useCallback((choice: WordChoice, displayedChoices: WordChoice[]) => {
        clearFooterList()
        const promptInputComponentCopy = (
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} flexWrap="wrap" rowGap={1}>
                {displayedChoices.map((displayedChoice) =>
                    renderWordRef.current(displayedChoice, displayedChoices, false, choice)
                )}
            </Stack>
        )

        if (choice.correct) {
            const newChatBubble = (
                <ChatBubble
                    isTeacher={true}
                    message={dialogPrompt.yes_response}
                    messageAudio={yesResponseAudio ? yesResponseAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setChatBubbleCorrectDone}
                />
            );
            setComponentList((prevComponentList) => [...prevComponentList, promptInputComponentCopy, newChatBubble]);
            UserDialogPromptApi.addCorrectAnswer(dialogPrompt.id)
                .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
        } else {
            setChatBubbleIncorrectDone(false)
            clearFooterList()
            const newChatBubble = (
                <ChatBubble
                    isTeacher={true}
                    message={dialogPrompt.no_response}
                    messageAudio={noResponseAudio ? noResponseAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setChatBubbleIncorrectDone}
                />
            );
            setComponentList((prevComponentList) => [...prevComponentList, promptInputComponentCopy, newChatBubble]);
            UserDialogPromptApi.addIncorrectAnswer(dialogPrompt.id)
                .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
        }
    }, [clearFooterList, dialogPrompt.yes_response, dialogPrompt.no_response, dialogPrompt.id, yesResponseAudio, noResponseAudio, showAvatar, chatBubbleContainerRef, setErrorMessage, history, location]);

    const renderWord = React.useCallback((
        choice: WordChoice,
        displayedChoices: WordChoice[],
        interactive: boolean,
        selectedChoice?: WordChoice,
    ) => {

        const circleContainerStyles = {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '56px',
            cursor: interactive ? 'pointer' : 'default',
            fontSize: choice.word.length > 1 ? '24px' : '48px',
            fontFamily: 'KaiTi',
            whiteSpace: 'nowrap',
        };

        const selected = selectedChoice?.id === choice.id;
        const wordNode = interactive ? (
            <Button
                key={choice.id}
                type="button"
                aria-label={choice.word}
                onClick={() => handleWordClick(choice, displayedChoices)}
                sx={{
                    ...circleContainerStyles,
                    px: 1,
                    mx: 1,
                    minWidth: 0,
                    color: 'inherit',
                    textTransform: 'none',
                    lineHeight: 1,
                }}
            >
                {choice.word}
            </Button>
        ) : (
            <Box
                key={choice.id}
                style={circleContainerStyles}
                sx={{ px: 1, mx: 1 }}
            >
                {choice.word}
            </Box>
        );

        return (
            <Box
                key={choice.id}
                sx={selected ? {
                    backgroundColor: hexToRgbaColorString(choice.correct ? theme.palette.success.main : theme.palette.error.main, 0.05),
                } : undefined}
            >
                {wordNode}
            </Box>
        );
    }, [theme.palette.success.main, theme.palette.error.main, handleWordClick]);

    renderWordRef.current = renderWord;

    React.useEffect(() => {
        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.yes_response, setYesResponseAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.no_response, setNoResponseAudio, isSettings)
    }, [clearFooterList, dialogPrompt.prompt, dialogPrompt.yes_response, dialogPrompt.no_response, history, isSettings, setErrorMessage]);

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
        }
    }, [componentList, chatBubbleContainerRef])

    const showWordChoices = React.useCallback(() => {
        const displayedChoices = shuffle(wordChoices);
        setActionList(displayedChoices.map((choice) => renderWord(choice, displayedChoices, true)));
    }, [wordChoices, setActionList, renderWord])

    React.useEffect(() => {
        if (chatBubbleDone) {
            showWordChoices()
        }
    }, [chatBubbleDone, showWordChoices])

    React.useEffect(() => {
        if (chatBubbleCorrectDone) {
            setDialogPromptComplete(dialogPrompt.id)
        }
    }, [chatBubbleCorrectDone, dialogPrompt.id, setDialogPromptComplete])

    React.useEffect(() => {
        if (chatBubbleIncorrectDone) {
            showWordChoices()
        }
    }, [chatBubbleIncorrectDone, showWordChoices])

    return (
        <Stack>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                setChatBubbleDone={setChatBubbleDone}
                chatBubbleContainerRef={chatBubbleContainerRef}
            />
            {componentList.map((component, index) => (
                <Box key={index} component="div">
                    {component}
                </Box>
            ))}
        </Stack>
    );
};

export default DialogPromptQuizWordComponent;
